import { useEffect, useState } from 'react'

import { getApplicationDisclosures } from 'api/application'
import { ApplicationDisclosure } from 'api/application/interfaces/ClientApplication'
import { AxiosError } from 'axios'
import { OctaneLogger } from 'integrations/datadog/OctaneLogger'

export interface UseApplicationDisclosureInterface {
    applicationDisclosures?: ApplicationDisclosure[]
    applicationDisclosureText?: string
    loading: boolean
    error: Error | null
}

const getDisclosureText = (
    disclosureName: string,
    disclosures?: ApplicationDisclosure[],
): string | undefined => {
    if (!disclosures?.length) {
        return undefined
    }

    const disclosure = disclosures.find(
        (item) => item.disclosure_name === disclosureName,
    )

    return disclosure?.disclosure_text
}

const useApplicationDisclosures = (
    applicationId: string | number,
    disclosureName?: string,
): UseApplicationDisclosureInterface => {
    const [applicationDisclosures, setApplicationDisclosures] = useState(
        {} as ApplicationDisclosure[],
    )

    const [applicationDisclosureText, setApplicationDisclosureText] =
        useState<string>()

    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<Error | null>(null)

    useEffect(() => {
        const abortController = new AbortController()

        const loadApplicationDisclosure = async () => {
            return getApplicationDisclosures(
                applicationId ?? '',
                abortController,
            )
        }

        if (!applicationId) {
            return
        }

        loadApplicationDisclosure()
            .then((disclosures: ApplicationDisclosure[]) => {
                setError(null)
                setLoading(false)
                setApplicationDisclosures(disclosures)

                if (disclosureName) {
                    const text = getDisclosureText(disclosureName, disclosures)

                    setApplicationDisclosureText(text)
                }
            })
            .catch((applicationDisclosureError) => {
                if (
                    (applicationDisclosureError as AxiosError).code ===
                    AxiosError.ERR_CANCELED
                ) {
                    return
                }

                OctaneLogger.error(
                    'Error when getting application disclosure.',
                    { application: { id: applicationId } },
                    applicationDisclosureError as Error,
                )

                setError(applicationDisclosureError)
                setLoading(false)
            })

        return () => abortController.abort()
    }, [applicationId, disclosureName])

    return {
        applicationDisclosures,
        applicationDisclosureText,
        loading,
        error,
    }
}

export default useApplicationDisclosures
